import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const FN_URL = `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe`;

type Status =
  | "validating"
  | "ready"
  | "submitting"
  | "success"
  | "already"
  | "invalid"
  | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("validating");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${FN_URL}?token=${encodeURIComponent(token)}`, {
          headers: { apikey: SUPABASE_ANON },
        });
        const data = await res.json();
        if (res.ok && data.valid) setStatus("ready");
        else if (data?.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch {
        setStatus("error");
      }
    })();
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setStatus("submitting");
    try {
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON,
          Authorization: `Bearer ${SUPABASE_ANON}`,
        },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <SiteLayout>
      <section className="min-h-[60vh] bg-background py-24">
        <div className="mx-auto max-w-xl px-6 text-center">
          <h1 className="font-serif text-4xl text-foreground">Email Preferences</h1>
          <div className="mt-8 text-base leading-relaxed text-muted-foreground">
            {status === "validating" && <p>Verifying your unsubscribe link…</p>}
            {status === "ready" && (
              <>
                <p className="mb-8">
                  Click below to unsubscribe from Hadassah Homes emails. You can always
                  reach out to us directly at Info@hadassahhomesinc.com.
                </p>
                <button
                  onClick={handleConfirm}
                  className="bg-foreground px-8 py-3 text-sm uppercase tracking-wider text-background transition hover:opacity-90"
                >
                  Confirm Unsubscribe
                </button>
              </>
            )}
            {status === "submitting" && <p>Processing…</p>}
            {status === "success" && (
              <p>You've been unsubscribed. We're sorry to see you go.</p>
            )}
            {status === "already" && <p>This email is already unsubscribed.</p>}
            {status === "invalid" && (
              <p>This unsubscribe link is invalid or has expired.</p>
            )}
            {status === "error" && (
              <p>Something went wrong. Please email Info@hadassahhomesinc.com.</p>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Unsubscribe;
